import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { PendingApprovalsProvider } from './contexts/PendingApprovalsContext';
import { PendingStorefrontsProvider } from './contexts/PendingStorefrontsContext';
import { AppLayout } from './components/layout/AppLayout';
import { Toaster } from './components/ui/Toaster';
import { Login } from './pages/auth/Login';
import { Dashboard } from './pages/dashboard/Dashboard';
import { Users } from './pages/users/Users';
import { Transactions } from './pages/transactions/Transactions';
import { Config } from './pages/config/Config';
import { Plans } from './pages/plans/Plans';
import { Agents } from './pages/agents/Agents';
import { Finance } from './pages/finance/Finance';
import { AuditLogs } from './pages/audit-logs/AuditLogs';
import { Admins } from './pages/admins/Admins';
import { Vendors } from './pages/vendors/Vendors';
import { VendorDetail } from './pages/vendors/VendorDetail';
import { ShopCategories } from './pages/shop-categories/ShopCategories';
import { Notifications } from './pages/notifications/Notifications';
import { ContentPages } from './pages/content/ContentPages';
import { HelpCentre } from './pages/help/HelpCentre';
import { ProductRequests } from './pages/product-requests/ProductRequests';
import { Requests } from './pages/requests/Requests';
import { KnowledgeGaps } from './pages/knowledge-gaps/KnowledgeGaps';
import { KnowledgeDocuments } from './pages/knowledge-documents/KnowledgeDocuments';
import { IbraEvals } from './pages/ibra/IbraEvals';
import { IbraNuggets } from './pages/ibra/IbraNuggets';
import { MoneyFigures } from './pages/ibra/MoneyFigures';
import { StorefrontReview } from './pages/storefronts/StorefrontReview';
import { StorefrontDomains } from './pages/storefronts/StorefrontDomains';
import { BusinessKyb } from './pages/business/BusinessKyb';
import { BillAddressBackfill } from './pages/bills/BillAddressBackfill';
import { Referrals } from './pages/referrals/Referrals';
import { Waitlist } from './pages/waitlist/Waitlist';
import { CardWaitlist } from './pages/waitlist/CardWaitlist';
import { ErrorMonitoring } from './pages/monitoring/ErrorMonitoring';
import { BusinessReconciliation } from './pages/business/BusinessReconciliation';
import { SavingsReconciliation } from './pages/savings/SavingsReconciliation';
import { ConsumerReconciliation } from './pages/consumer/ConsumerReconciliation';
import { Aggregators } from './pages/aggregators/Aggregators';
import { BusinessRegistrations } from './pages/business/BusinessRegistrations';
import { BusinessInvoices } from './pages/business/BusinessInvoices';
import { BusinessAccounts } from './pages/business/BusinessAccounts';
import { Businesses } from './pages/business/Businesses';
import { Tier3Verifications } from './pages/kyc/Tier3Verifications';
import { SupportChat } from './pages/support/SupportChat';
import { Settings } from './pages/settings/Settings';
import { Ajo } from './pages/ajo/Ajo';
import { Roles } from './pages/roles/Roles';
import { RiskEvents } from './pages/fraud/RiskEvents';
import { Approvals } from './pages/approvals/Approvals';
import { CaseQueue } from './pages/compliance/CaseQueue';
import { RegulatoryReports } from './pages/compliance/RegulatoryReports';
import { MfaSetup } from './pages/compliance/MfaSetup';
import { ScreeningReview } from './pages/compliance/ScreeningReview';
import { RiskProfile } from './pages/compliance/RiskProfile';
import { AccountRecoveryQueue } from './pages/compliance/AccountRecoveryQueue';
import { CacClaimQueue } from './pages/compliance/CacClaimQueue';
import { SigningMandateReview } from './pages/compliance/SigningMandateReview';
import { Payroll } from './pages/payroll/Payroll';
import { AiSettingsPage } from './pages/ai-settings/AiSettingsPage';

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <PendingApprovalsProvider>
        <PendingStorefrontsProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route element={<AppLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/compliance/cases" element={<CaseQueue />} />
                <Route path="/compliance/reports" element={<RegulatoryReports />} />
                <Route path="/compliance/screening" element={<ScreeningReview />} />
                <Route
                  path="/compliance/account-recovery"
                  element={<AccountRecoveryQueue />}
                />
                <Route
                  path="/compliance/signing-mandates"
                  element={<SigningMandateReview />}
                />
                <Route
                  path="/compliance/cac-claims"
                  element={<CacClaimQueue />}
                />
                <Route path="/compliance/risk/:subjectType/:subjectId" element={<RiskProfile />} />
                {/* Not permission-gated: an admin must be able to enrol
                    before holding any permission that requires MFA, or the
                    requirement is unsatisfiable. */}
                <Route path="/security/two-factor" element={<MfaSetup />} />
                <Route path="/finance" element={<Finance />} />
                <Route path="/revenue" element={<Navigate to="/finance" replace />} />
                <Route path="/plans" element={<Plans />} />
                <Route path="/ai-settings" element={<AiSettingsPage />} />
                <Route path="/users" element={<Users />} />
                <Route path="/transactions" element={<Transactions source="payments" />} />
                <Route path="/shop-transactions" element={<Transactions source="shop" />} />
                <Route
                  path="/business-transactions"
                  element={<Transactions source="business" />}
                />
                <Route path="/referrals" element={<Referrals />} />
                <Route path="/waitlist" element={<Waitlist />} />
                <Route path="/card-waitlist" element={<CardWaitlist />} />
                <Route path="/error-monitoring" element={<ErrorMonitoring />} />
                <Route path="/business-reconciliation" element={<BusinessReconciliation />} />
                <Route path="/savings-reconciliation" element={<SavingsReconciliation />} />
                <Route path="/consumer-reconciliation" element={<ConsumerReconciliation />} />
                <Route path="/aggregators" element={<Aggregators />} />
                <Route path="/business-registrations" element={<BusinessRegistrations />} />
                <Route path="/business-invoices" element={<BusinessInvoices />} />
                <Route path="/business-accounts" element={<BusinessAccounts />} />
                <Route path="/businesses" element={<Businesses />} />
                <Route path="/tier3-verifications" element={<Tier3Verifications />} />
                <Route path="/ajo" element={<Ajo />} />
                <Route path="/vendors" element={<Vendors />} />
                <Route path="/vendors/:id" element={<VendorDetail />} />
                <Route path="/shop-categories" element={<ShopCategories />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/content" element={<ContentPages />} />
                <Route path="/help-centre" element={<HelpCentre />} />
                <Route path="/product-requests" element={<ProductRequests />} />
                <Route path="/payment-requests" element={<Requests />} />
                <Route path="/knowledge-gaps" element={<KnowledgeGaps />} />
                <Route path="/knowledge-base" element={<KnowledgeDocuments />} />
                <Route path="/ibra-evals" element={<IbraEvals />} />
                <Route path="/ibra-nuggets" element={<IbraNuggets />} />
                <Route path="/money-figures" element={<MoneyFigures />} />
                <Route path="/storefront-review" element={<StorefrontReview />} />
                <Route path="/storefront-domains" element={<StorefrontDomains />} />
                <Route path="/businesses/:businessId/kyb" element={<BusinessKyb />} />
                <Route path="/bill-addresses" element={<BillAddressBackfill />} />
                <Route path="/support" element={<SupportChat />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/config" element={<Config />} />
                <Route path="/agents" element={<Agents />} />
                <Route path="/risk" element={<RiskEvents />} />
                <Route path="/approvals" element={<Approvals />} />
                <Route path="/payroll" element={<Payroll />} />
                <Route path="/audit-logs" element={<AuditLogs />} />
                <Route path="/admins" element={<Admins />} />
                <Route path="/roles" element={<Roles />} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            {/* Inside the router: toasts can carry react-router links. */}
            <Toaster />
          </BrowserRouter>
        </PendingStorefrontsProvider>
        </PendingApprovalsProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
